import { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { getAllDebts } from '../services/debtService';
import type { Debt, DebtCategory } from '../types/debt';
import { BarChart2, Building, CheckCircle2, Clock } from 'lucide-react';

const CATEGORY_COLORS: Record<DebtCategory, string> = {
  credit_card: '#818cf8',      // indigo
  personal_loan: '#34d399',    // emerald
  buy_now_pay_later: '#f59e0b',// amber
  mortgage: '#f87171',         // rose
  auto_loan: '#67e8f9',        // cyan
  other: '#a78bfa',            // violet
};

const CATEGORY_LABELS: Record<DebtCategory, string> = {
  credit_card: 'Credit Card',
  personal_loan: 'Personal Loan',
  buy_now_pay_later: 'Buy Now Pay Later',
  mortgage: 'Mortgage',
  auto_loan: 'Auto Loan',
  other: 'Other',
};

const Analytics = () => {
  const [debts, setDebts] = useState<Debt[]>([]);

  useEffect(() => { setDebts(getAllDebts()); }, []);

  const active = debts.filter((d) => d.status !== 'paid');
  const paid = debts.filter((d) => d.status === 'paid');

  // Category breakdown
  const catTotals: Partial<Record<DebtCategory, number>> = {};
  for (const d of active) {
    catTotals[d.category] = (catTotals[d.category] || 0) + (d.amount - d.amountPaid);
  }
  const sortedCats = Object.entries(catTotals).sort(([, a], [, b]) => b - a) as [DebtCategory, number][];
  const maxCat = sortedCats[0]?.[1] || 1;

  // Top creditors
  const creditorTotals: Record<string, number> = {};
  for (const d of active) {
    if (!creditorTotals[d.creditorName]) creditorTotals[d.creditorName] = 0;
    creditorTotals[d.creditorName] += (d.amount - d.amountPaid);
  }
  const topCreditors = Object.entries(creditorTotals)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6);

  const chartBarClass = "h-full rounded-full transition-all duration-700";

  return (
    <div className="min-h-screen flex" style={{ background: '#0f0f13' }}>
      <Sidebar />
      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="p-6 max-w-5xl mx-auto">

          <div className="mb-8 fade-in-up">
            <div className="flex items-center gap-2 mb-1">
              <BarChart2 className="h-5 w-5 text-indigo-400" />
              <h1 className="text-2xl font-bold text-white">Analytics</h1>
            </div>
            <p className="text-sm text-gray-500">A high-level view of your debt activity</p>
          </div>

          {/* Summary row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8 fade-in-up delay-100">
            {[
              { label: 'Total Accounts', value: String(debts.length), sub: 'All time', icon: BarChart2, color: 'text-indigo-400 bg-indigo-400/10' },
              { label: 'Active', value: String(active.length), sub: 'Pending / partial', icon: Clock, color: 'text-amber-400 bg-amber-400/10' },
              { label: 'Settled', value: String(paid.length), sub: 'Fully paid off', icon: CheckCircle2, color: 'text-emerald-400 bg-emerald-400/10' },
              { label: 'Unique Creditors', value: String(Object.keys(creditorTotals).length + paid.reduce((s, d) => { s.add(d.creditorName); return s; }, new Set<string>()).size), sub: 'Banks & Services', icon: Building, color: 'text-violet-400 bg-violet-400/10' },
            ].map((item) => (
              <div key={item.label} className="glass-card rounded-2xl p-4">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center mb-3 ${item.color}`}>
                  <item.icon className="h-4 w-4" />
                </div>
                <p className="text-2xl font-bold text-white">{item.value}</p>
                <p className="text-xs text-gray-500 mt-1">{item.label}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Category breakdown */}
            <div className="glass-card rounded-2xl p-6 fade-in-up delay-300">
              <h2 className="text-sm font-semibold text-white mb-5">By Category <span className="text-gray-600 font-normal text-xs">(active only)</span></h2>
              {sortedCats.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center">No active debts to display.</p>
              ) : (
                <div className="space-y-4">
                  {sortedCats.map(([cat, total]) => (
                    <div key={cat}>
                      <div className="flex justify-between text-xs mb-1.5">
                        <span className="text-gray-400">{CATEGORY_LABELS[cat]}</span>
                        <span className="text-white font-medium">USD {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <div className={chartBarClass} style={{ width: `${(total / maxCat) * 100}%`, background: CATEGORY_COLORS[cat] }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top creditors */}
            <div className="glass-card rounded-2xl p-6 fade-in-up delay-400">
              <h2 className="text-sm font-semibold text-white mb-5">Top Creditors <span className="text-gray-600 font-normal text-xs">(by active balance)</span></h2>
              {topCreditors.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center">No active creditors.</p>
              ) : (
                <div className="grid gap-3">
                  {topCreditors.map(([name, activeBalance]) => (
                    <div key={name} className="flex items-center justify-between p-3 rounded-xl bg-white/3 border border-white/5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-xl bg-indigo-500/20 flex items-center justify-center text-xs font-bold text-indigo-400">
                          {name.substring(0, 2).toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-white">{name}</span>
                      </div>
                      <div className="text-right text-sm">
                        <p className="text-white font-medium">USD {activeBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Analytics;
